<template>
  <v-dialog v-model="dialogVisible" max-width="560px" persistent>
    <v-card>
      <v-card-title class="headline"> Estender Período da Etapa </v-card-title>

      <v-card-text>
        <v-alert type="info" text dense class="text-caption">
          A <strong>antecipação do início</strong> vale apenas para o lançamento de aulas — notas e
          planos de curso só liberam a partir do início original da etapa. A
          <strong>prorrogação do fim</strong> vale para todos: aulas, notas e planos.
        </v-alert>

        <div class="mb-4">
          <p class="body-1 mb-2"><strong>Etapa:</strong> {{ etapaNome }}</p>
          <p class="body-2 text--secondary mb-0">
            Período original: {{ formatar(periodoInicial) }} à {{ formatar(periodoFinal) }}
          </p>
        </div>

        <v-menu
          v-model="pickerInicialVisible"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formatar(novaDataInicial)"
              label="Antecipar início para"
              prepend-icon="mdi-calendar-arrow-left"
              :append-icon="novaDataInicial ? 'mdi-close' : ''"
              @click:append.stop="novaDataInicial = null"
              hint="Vale apenas para lançamento de aulas"
              persistent-hint
              readonly
              v-bind="attrs"
              v-on="on"
              outlined
              dense
            ></v-text-field>
          </template>

          <v-date-picker
            v-model="novaDataInicial"
            :max="dataMaximaInicio"
            @input="pickerInicialVisible = false"
            locale="pt-br"
            first-day-of-week="0"
          ></v-date-picker>
        </v-menu>

        <v-menu
          v-model="pickerFinalVisible"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formatar(novaData)"
              label="Estender fim até"
              prepend-icon="mdi-calendar-arrow-right"
              :append-icon="novaData ? 'mdi-close' : ''"
              @click:append.stop="novaData = null"
              readonly
              v-bind="attrs"
              v-on="on"
              outlined
              dense
              class="mt-4"
            ></v-text-field>
          </template>

          <v-date-picker
            v-model="novaData"
            :min="dataMinimaFim"
            @input="pickerFinalVisible = false"
            locale="pt-br"
            first-day-of-week="0"
          ></v-date-picker>
        </v-menu>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="fecharModal" :disabled="carregando"> Cancelar </v-btn>
        <v-btn
          color="green darken-3"
          dark
          @click="salvarExtensao"
          :loading="carregando"
          :disabled="!houveAlteracao"
        >
          <v-icon left>mdi-check</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { dateFormatParam } from "@/plugins/filters";

export default {
  name: "ModalEstenderEtapa",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    etapaId: {
      type: [String, Number],
      default: null,
    },
    etapaNome: {
      type: String,
      default: "",
    },
    dataExtensao: {
      type: String,
      default: null,
    },
    dataExtensaoInicial: {
      type: String,
      default: null,
    },
    periodoInicial: {
      type: String,
      default: null,
    },
    periodoFinal: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      dialogVisible: false,
      pickerInicialVisible: false,
      pickerFinalVisible: false,
      carregando: false,
      novaData: this.dataExtensao,
      novaDataInicial: this.dataExtensaoInicial,
    };
  },

  computed: {
    dataMaximaInicio() {
      if (!this.periodoInicial) return null;
      return this.somarDias(this.periodoInicial, -1);
    },

    // Prorrogar só faz sentido depois do fim da etapa e a partir de hoje.
    dataMinimaFim() {
      const referencia =
        this.periodoFinal && this.periodoFinal > this.hoje ? this.periodoFinal : this.hoje;
      return this.somarDias(referencia, 1);
    },

    hoje() {
      const agora = new Date();
      const mes = `${agora.getMonth() + 1}`.padStart(2, "0");
      const dia = `${agora.getDate()}`.padStart(2, "0");
      return `${agora.getFullYear()}-${mes}-${dia}`;
    },

    houveAlteracao() {
      return (
        (this.novaData || null) !== (this.dataExtensao || null) ||
        (this.novaDataInicial || null) !== (this.dataExtensaoInicial || null)
      );
    },
  },

  watch: {
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal;
        if (newVal) {
          this.resetarFormulario();
        }
      },
      immediate: true,
    },

    dialogVisible(newVal) {
      if (!newVal && this.visible) {
        this.$emit("fechar");
      }
    },
  },

  methods: {
    formatar(data) {
      if (!data) return "";
      return dateFormatParam(data, "d/m/y");
    },

    somarDias(dataIso, dias) {
      const [ano, mes, dia] = dataIso.split("-").map(Number);
      const data = new Date(ano, mes - 1, dia + dias);
      const mesFormatado = `${data.getMonth() + 1}`.padStart(2, "0");
      const diaFormatado = `${data.getDate()}`.padStart(2, "0");
      return `${data.getFullYear()}-${mesFormatado}-${diaFormatado}`;
    },

    resetarFormulario() {
      this.novaData = this.dataExtensao;
      this.novaDataInicial = this.dataExtensaoInicial;
    },

    fecharModal() {
      this.dialogVisible = false;
      this.$emit("fechar");
    },

    descricaoAlteracoes() {
      const partes = [];

      if ((this.novaDataInicial || null) !== (this.dataExtensaoInicial || null)) {
        partes.push(
          this.novaDataInicial
            ? `antecipar o início para <strong>${this.formatar(this.novaDataInicial)}</strong>`
            : "remover a antecipação do início"
        );
      }

      if ((this.novaData || null) !== (this.dataExtensao || null)) {
        partes.push(
          this.novaData
            ? `estender o fim até <strong>${this.formatar(this.novaData)}</strong>`
            : "remover a prorrogação do fim"
        );
      }

      return partes.join(" e ");
    },

    salvarExtensao() {
      if (!this.etapaId || !this.houveAlteracao) {
        return;
      }

      this.$modals.warn({
        title: "Cuidado!",
        message: `Tem certeza que deseja ${this.descricaoAlteracoes()} na etapa <strong>${
          this.etapaNome
        }</strong>?`,
        confirmationCode: this.etapaId,
        confirmationMessage: `Por favor, digite <strong>${this.etapaId}</strong> para confirmar`,
        buttons: [
          {
            text: "Cancelar",
            callback: async () => {
              this.$modals.close();
              this.fecharModal();
            },
            props: { color: "warning", small: true },
          },
          {
            text: "Confirmar",
            disableIfCodeDoesntMatch: true,
            callback: async () => {
              this.$modals.close();
              this.carregando = true;
              try {
                await this.$services.etapasService.estenderEtapa(this.etapaId, {
                  periodo_extensao: this.novaData,
                  periodo_extensao_inicial: this.novaDataInicial,
                });

                this.$emit("sucesso", {
                  etapaId: this.etapaId,
                  novaData: this.novaData,
                  novaDataInicial: this.novaDataInicial,
                });

                this.$toast.success("Extensão da etapa atualizada com sucesso!");
              } catch (error) {
                this.$handleError(error);
              } finally {
                this.fecharModal();
                this.carregando = false;
              }
            },
            props: { color: "green", dark: true, small: true },
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.headline {
  padding-bottom: 16px !important;
}

.v-card__text {
  padding-top: 0 !important;
}
</style>
